// Шпаргалка изотерм: какие температуры сейчас в сезонной модели пелагиков.
//
// Свёрнута по умолчанию и спрашивает сервер только раскрытой: изотерму ставят
// часто, а шпаргалку открывают изредка. Ключ запроса — МЕСЯЦ, а не день: модель
// помесячная, и смена даты внутри месяца ответа не меняет.
//
// Нажатие на край диапазона ставит основную изотерму на это число (в границы
// ползунка и на его шаг). Край за ползунком помечен: сельдь с низом −1,5 °C
// встанет на 0 — молча это читалось бы как опечатка.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import type { ThermalBand } from '../api'
import { formatNumber, useLang, useT } from '../i18n'
import { speciesName } from '../i18n/apiLabels'
import { cheatRows, depthLabel, isoPick } from './thermalHints'

interface Props {
  /** Дата вида (YYYY-MM-DD) — от неё месяц модели. */
  date: string
  species: number | null
  min: number
  max: number
  onPick: (value: number) => void
}

export function ThermalCheatSheet({ date, species, min, max, onPick }: Props) {
  const t = useT()
  const { lang } = useLang()
  const [open, setOpen] = useState(false)
  const monthDate = `${date.slice(0, 7)}-01`
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cabinet', 'thermal-hints', monthDate],
    queryFn: () => cabinetApi.thermalHints(monthDate),
    enabled: open,
    staleTime: 60 * 60 * 1000,
  })
  const rows = useMemo(() => cheatRows(data, species), [data, species])
  const fmt = (v: number) => formatNumber(lang, v, 1)
  const monthName = useMemo(() => {
    const d = new Date(`${monthDate}T00:00:00Z`)
    return Number.isNaN(d.getTime())
      ? ''
      : new Intl.DateTimeFormat(lang, { month: 'long', timeZone: 'UTC' }).format(d)
  }, [lang, monthDate])

  const Edge = ({ v }: { v: number | null }) => {
    const target = isoPick(v, min, max)
    if (v == null || target == null) return <span className="text-faint">—</span>
    const clamped = target !== v
    const label = clamped
      ? t('maps.isotherm.cheat.clamped', { value: fmt(target) })
      : t('maps.isotherm.cheat.pick', { value: fmt(target) })
    return (
      <button
        type="button"
        onClick={() => onPick(target)}
        title={label}
        aria-label={label}
        className={
          'tnum rounded px-0.5 hover:bg-hull hover:text-foam ' +
          (clamped ? 'text-faint underline decoration-dotted' : 'text-foam')
        }
      >
        {fmt(v)}
      </button>
    )
  }

  const Band = ({ band }: { band: ThermalBand }) => (
    <div className="space-y-0.5">
      <div className="flex items-baseline gap-1">
        <span className="w-14 shrink-0 text-[10px] text-faint">{t('maps.isotherm.cheat.preferred')}</span>
        <Edge v={band.preferred[0]} />
        <span className="text-faint">–</span>
        <Edge v={band.preferred[1]} />
      </div>
      <div className="flex items-baseline gap-1">
        <span className="w-14 shrink-0 text-[10px] text-faint">{t('maps.isotherm.cheat.possible')}</span>
        <Edge v={band.possible[0]} />
        <span className="text-faint">–</span>
        <Edge v={band.possible[1]} />
      </div>
    </div>
  )

  return (
    <div className="border-t border-hull pt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-1.5 text-left text-[11px] font-medium text-mist"
      >
        <span aria-hidden className={'transition-transform ' + (open ? 'rotate-90' : '')}>
          ›
        </span>
        {t('maps.isotherm.cheat.title')}
      </button>
      {open && (
        <div className="mt-1.5 text-[11px] text-mist">
          <div className="mb-1.5 text-[10px] text-faint">
            {t('maps.isotherm.cheat.intro', { month: monthName })}
          </div>
          {isLoading && <div className="text-faint">{t('maps.isotherm.cheat.loading')}</div>}
          {isError && <div className="text-faint">{t('maps.isotherm.cheat.error')}</div>}
          {data && rows.length === 0 && (
            <div className="text-faint">{t('maps.isotherm.cheat.empty')}</div>
          )}
          {rows.length > 0 && (
            <div className="space-y-1">
              {rows.map(row => (
                <div
                  key={row.speciesId}
                  aria-current={row.selected || undefined}
                  className={
                    'rounded px-1 py-1 ' + (row.selected ? 'bg-hull ring-1 ring-sonar' : '')
                  }
                >
                  <div className="mb-0.5 flex items-baseline gap-1">
                    <span className={row.selected ? 'font-semibold text-foam' : 'text-foam'}>
                      {speciesName(row.species, lang)}
                    </span>
                    {row.yearRound && (
                      <span className="text-[10px] text-faint">{t('maps.isotherm.cheat.yearRound')}</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {row.cells.map(cell => (
                      <div key={cell.depths.join('-')}>
                        <div className="tnum text-[10px] text-faint">
                          {t('maps.isotherm.cheat.depth', { depth: depthLabel(cell.depths) })}
                        </div>
                        {cell.band ? <Band band={cell.band} /> : null}
                        {cell.sourceKey && (
                          <div className="text-[10px] text-faint">{t(cell.sourceKey)}</div>
                        )}
                        {cell.reasonKey && (
                          <div className="text-[10px] text-faint">{t(cell.reasonKey)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  {row.noteKey && <div className="mt-0.5 text-[10px] text-faint">{t(row.noteKey)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
